import { useState } from "react";
import { useContextoApp } from "@/app/contexto";
import { useApiResource } from "@/hooks/useApiResource";
import { useCartelInstalar } from "@/hooks/useCartelInstalar";
import type { BadgeTono, FotoGaleria } from "@/components";
import type { Avance, CuentaMia, Fideicomiso, ListadoGastos, ListadoNoticias, UnidadProyecto } from "@/lib/api";
import { MENSAJE_SIN_FICHA } from "@/lib/aportes";
import { fotosGaleria, tarjetaAvance } from "@/lib/avance";
import { TONO_SALDO, cuentaDelSaldo, estadoSaldo, formatSaldo, tarjetaFaltaAportar } from "@/lib/cuentas";
import { formatFecha, formatMoneda } from "@/lib/format";
import { tarjetaGastos } from "@/lib/gastos";
import { ETIQUETA_TIPO_NOTICIA, NOTICIAS_POR_PAGINA, TONO_TIPO_NOTICIA, rutaNoticia } from "@/lib/noticias";
import { pisosAnexos, tarjetaProyecto, unidadesVisibles } from "@/lib/proyecto";

export interface NoticiaVista {
  clave: string;
  fecha: string;
  etiqueta: string;
  tono: BadgeTono;
  titulo: string;
  texto: string | null;
  fotos: FotoGaleria[];
  /** Adónde lleva tocarla (ficha del gasto, avance, proyecto, cuenta); null si no hay más para ver. */
  ruta: string | null;
}

/** Una tarjeta del tablero: toda se toca y lleva a su pestaña. */
export interface TarjetaTablero {
  titulo: string;
  valor: string;
  detalle: string;
  ruta: string;
  /** Sólo en la de avance: la barra. */
  porcentaje?: number | null;
}

export interface TarjetaCuenta {
  valor: string;
  estado: string;
  tono: BadgeTono;
  aportado: string;
  /** Lo que le falta aportar de las previsiones (Mati, 2026-10-20); null si no debe nada. */
  faltaAportar: { valor: string; tono: BadgeTono; detalle: string } | null;
}

const NO_CARGO = "No se pudo cargar";

/**
 * Inicio del fiduciante (sin JSX): un tablero con un poco de cada pestaña (Mati, 2026-10-20). Su cuenta en dos números
 * (estado de cuenta corriente y lo aportado), avance de obra, gastos y proyecto, y las novedades del emprendimiento.
 * Todo se toca: cada tarjeta lleva a su pestaña y cada novedad a su detalle. Nada de caja ni de los demás fiduciantes.
 */
export function useNovedadesTab() {
  const { usuario, membresia, fid } = useContextoApp();
  const cartel = useCartelInstalar(membresia.rol);
  const [limite, setLimite] = useState(NOTICIAS_POR_PAGINA);
  const cuenta = useApiResource<CuentaMia>(`/api/fideicomisos/${fid}/cuentas/mia`);
  const noticias = useApiResource<ListadoNoticias>(`/api/fideicomisos/${fid}/noticias?limite=${limite}`);
  const avance = useApiResource<Avance>(`/api/fideicomisos/${fid}/avance`);
  const gastos = useApiResource<ListadoGastos>(`/api/fideicomisos/${fid}/gastos`);
  const proyecto = useApiResource<UnidadProyecto[]>(`/api/fideicomisos/${fid}/unidades/proyecto`);
  // Para no contar lo de los pisos anexos (Newbery 3563: el depto de prueba del 8), como en Proyecto
  const fideicomiso = useApiResource<Fideicomiso>(`/api/fideicomisos/${fid}`);

  const tablero: TarjetaTablero[] = [];
  if (avance.data) tablero.push({ titulo: "Avance de obra", ruta: "/avance", ...tarjetaAvance(avance.data) });
  else if (avance.error) tablero.push({ titulo: "Avance de obra", ruta: "/avance", valor: "—", detalle: NO_CARGO });
  if (gastos.data) tablero.push({ titulo: "Gastos de la obra", ruta: "/gastos", ...tarjetaGastos(gastos.data) });
  else if (gastos.error) tablero.push({ titulo: "Gastos de la obra", ruta: "/gastos", valor: "—", detalle: NO_CARGO });
  if (proyecto.data && (fideicomiso.data || fideicomiso.error))
    tablero.push({ titulo: "Proyecto", ruta: "/proyecto", ...tarjetaProyecto(unidadesVisibles(proyecto.data, pisosAnexos(fideicomiso.data?.direccion ?? null))) });
  else if (proyecto.error) tablero.push({ titulo: "Proyecto", ruta: "/proyecto", valor: "—", detalle: NO_CARGO });

  let tarjeta: TarjetaCuenta | null = null;
  if (cuenta.data) {
    const estado = estadoSaldo(cuenta.data.saldo_usd);
    tarjeta = { valor: formatSaldo(cuenta.data.saldo_usd), estado: cuentaDelSaldo(cuenta.data), tono: TONO_SALDO[estado], aportado: formatMoneda(cuenta.data.aportado_usd, "USD"), faltaAportar: tarjetaFaltaAportar(cuenta.data) };
  }
  const errorCuenta = cuenta.error ? (cuenta.error.status === 404 ? MENSAJE_SIN_FICHA : "No se pudo cargar tu cuenta") : null;

  const items: NoticiaVista[] = (noticias.data?.items ?? []).map((n) => ({
    clave: n.clave,
    fecha: formatFecha(n.fecha),
    etiqueta: ETIQUETA_TIPO_NOTICIA[n.tipo],
    tono: TONO_TIPO_NOTICIA[n.tipo],
    titulo: n.titulo,
    texto: n.texto,
    fotos: fotosGaleria(fid, n),
    ruta: rutaNoticia(n),
  }));

  return {
    nombre: usuario.nombre.split(" ")[0] ?? usuario.nombre,
    cartelInstalar: cartel.visible,
    cerrarCartel: cartel.cerrar,
    tarjeta,
    errorCuenta,
    tablero,
    noticias: items,
    cargandoNoticias: !noticias.data && noticias.loading,
    errorNoticias: !noticias.data && noticias.error ? "No se pudieron cargar las novedades" : null,
    reintentarNoticias: noticias.recargar,
    hayMas: noticias.data?.hay_mas ?? false,
    cargandoMas: Boolean(noticias.data) && noticias.loading,
    verMas: () => setLimite((l) => l + NOTICIAS_POR_PAGINA),
  };
}
